import API_BASE from '../config';
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, Zap, X, ChevronRight, ShieldAlert } from "lucide-react";
import axios from "axios";

const API = `${API_BASE}`;

interface TrialStatus {
  is_trial:   boolean;
  is_expired: boolean;
  days_left:  number | null;
  trial_ends: string | null;
  tier:       string;
}

// ── Trial Banner — shows at top of dashboard when in trial ───────────────────
export function TrialBanner() {
  const [status, setStatus]   = useState<TrialStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${API}/api/auth/trial-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => setStatus(r.data)).catch(() => {});
  }, []);

  // Never show banner for paying subscribers
  // Only show for genuine free trial users who have NOT upgraded
  const isPaidSubscriber = status?.tier &&
    ["starter", "professional", "enterprise"].includes(
      status.tier.toLowerCase()
    ) && status.tier.toLowerCase() !== "standard";

  if (!status || !status.is_trial || dismissed || isPaidSubscriber) return null;

  const daysLeft = status.days_left ?? 0;
  const urgent   = daysLeft <= 2;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className={`relative rounded-[1.5rem] p-5 mb-6 flex items-center justify-between gap-4
          ${urgent
            ? "bg-red-600/10 border-2 border-red-600/30"
            : "bg-amber-500/10 border-2 border-amber-500/20"
          }`}
      >
        <div className="flex items-center gap-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
            ${urgent ? "bg-red-600/20" : "bg-amber-500/20"}`}>
            <Clock size={20} className={urgent ? "text-red-500" : "text-amber-400"} />
          </div>
          <div>
            <p className={`text-sm font-black uppercase tracking-wide
              ${urgent ? "text-red-400" : "text-amber-400"}`}>
              {daysLeft === 0
                ? "Your trial expires today"
                : daysLeft === 1
                ? "1 day left in your trial"
                : `${daysLeft} days left in your free trial`}
            </p>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              Upgrade to Professional or Enterprise to keep full access after your trial ends.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => window.location.href = "/settings#upgrade"}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-black text-xs
              uppercase tracking-widest text-white transition-all
              ${urgent ? "bg-red-600 hover:bg-red-700" : "bg-amber-500 hover:bg-amber-600"}`}
          >
            <Zap size={13} /> Upgrade Now
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="w-8 h-8 rounded-lg flex items-center justify-center
              text-slate-500 hover:text-white hover:bg-white/10 transition-all"
          >
            <X size={14} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

// ── Trial Expired Modal — blocks access when trial is over ───────────────────
export function TrialExpiredModal() {
  const [expired, setExpired] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) { setChecking(false); return; }

    axios.get(`${API}/api/auth/trial-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      if (r.data.is_trial && r.data.is_expired) {
        setExpired(true);
      }
      setChecking(false);
    }).catch(() => setChecking(false));
  }, []);

  if (checking || !expired) return null;

  const handleUpgrade = async (tier: string) => {
    const token = localStorage.getItem("token");
    if (!token) return;
    try {
      const res = await axios.post(
        `${API}/api/stripe/create-checkout`,
        { tier, billing: "monthly" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        document.location.assign(res.data.checkout_url);
      }
    } catch (err) {
      console.error("Checkout error:", err);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem("token");
    window.location.href = "/";
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center
          justify-center px-6"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-[#0f172a] border border-white/10 rounded-[2.5rem] p-10
            max-w-lg w-full shadow-2xl text-center"
        >
          {/* Icon */}
          <div className="w-16 h-16 bg-red-600/10 border border-red-600/20
            rounded-[1.5rem] flex items-center justify-center mx-auto mb-6">
            <ShieldAlert size={32} className="text-red-500" />
          </div>

          <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-500 mb-3">
            Trial Expired
          </p>
          <h2 className="text-3xl font-black text-white tracking-tighter uppercase mb-4">
            Your Free Trial<br />Has Ended.
          </h2>
          <p className="text-slate-400 text-sm font-medium leading-relaxed mb-8">
            Your 7-day free trial has expired. Upgrade to Professional or Enterprise
            to continue accessing VicShield and protect your organisation.
          </p>

          {/* Plan options */}
          <div className="space-y-3 mb-6">
            <button
              onClick={() => handleUpgrade("starter")}
              className="w-full flex items-center justify-between px-6 py-4
                bg-white/5 border border-white/10 rounded-2xl hover:bg-emerald-600/10
                hover:border-emerald-600/30 transition-all group"
            >
              <div className="text-left">
                <p className="text-sm font-black text-white uppercase tracking-wide">
                  Starter
                </p>
                <p className="text-xs text-slate-400 font-medium">
                  $199/mo · 1 entity · 13-page audit PDF
                </p>
              </div>
              <ChevronRight size={16} className="text-slate-600 group-hover:text-emerald-400" />
            </button>

            <button
              onClick={() => handleUpgrade("professional")}
              className="w-full flex items-center justify-between px-6 py-4
                bg-white/5 border border-white/10 rounded-2xl hover:bg-blue-600/10
                hover:border-blue-600/30 transition-all group"
            >
              <div className="text-left">
                <p className="text-sm font-black text-white uppercase tracking-wide">
                  Professional
                </p>
                <p className="text-xs text-slate-400 font-medium">
                  $499/mo · 2 entities · Full actuarial engine
                </p>
              </div>
              <ChevronRight size={16} className="text-slate-600 group-hover:text-blue-400" />
            </button>

            <button
              onClick={() => handleUpgrade("enterprise")}
              className="w-full flex items-center justify-between px-6 py-4
                bg-amber-500/10 border border-amber-500/20 rounded-2xl
                hover:bg-amber-500/20 transition-all group"
            >
              <div className="text-left">
                <p className="text-sm font-black text-amber-400 uppercase tracking-wide">
                  Enterprise
                </p>
                <p className="text-xs text-slate-400 font-medium">
                  $999/mo · 5 entities · Board deck · API access
                </p>
              </div>
              <ChevronRight size={16} className="text-slate-600 group-hover:text-amber-400" />
            </button>
          </div>

          <button
            onClick={handleSignOut}
            className="text-xs font-black uppercase tracking-widest text-slate-600
              hover:text-slate-400 transition-colors"
          >
            Sign Out
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
